import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export interface PedagogyDashboardRow {
  periodId: number
  specialistUserId: string
  specialistName: string | null
  tgUsername: string | null
  partnersCount: number
  studiosCount: number
  plannedReviewsCount: number
  factReviewsCount: number
  completionPct: number | null
  studiosWithoutPlanCount: number
  factsWithoutPlanCount: number
  lowQualityReviewsCount: number
  latestReviewDate: string | null
  pedagogyRoleEndDate: string | null
}

export interface PedagogyDepartmentQualitySummary {
  reviewsCount: number
  totalScore: number
  maxScore: number
  averageQualityPercent: number | null
}

export interface PedagogySpecialistPeriodRow {
  periodId: number
  specialistUserId: string
  studioInternalId: number
  studioNumber: string
  cityName: string
  address: string
  statusCode: string
  partnerNumber: number | null
  partnerPersonsFio: string | null
  partnerCitiesText: string | null
  plannedReviewsCount: number
  factReviewsCount: number
  latestReviewDate: string | null
  hasPlan: boolean
  inScope: boolean
  missingPlan: boolean
  factWithoutPlan: boolean
  closedButKept: boolean
}

export interface PedagogyWeeklyPlanMatrixRow {
  periodId: number
  studioInternalId: number
  studioNumber: string
  cityName: string
  address: string
  statusCode: string
  partnerNumber: number | null
  partnerPersonsFio: string | null
  partnerCitiesText: string | null
  inScope: boolean
  canPlanWeek1: boolean
  canPlanWeek2: boolean
  canPlanWeek3: boolean
  canPlanWeek4: boolean
  canPlanWeek5: boolean
  planWeek1: number
  planWeek2: number
  planWeek3: number
  planWeek4: number
  planWeek5: number
  factWeek1: number
  factWeek2: number
  factWeek3: number
  factWeek4: number
  factWeek5: number
  planTotal: number
  factTotal: number
}

export interface SavePedagogyWeeklyPlanRowInput {
  studioInternalId: number
  week1: number
  week2: number
  week3: number
  week4: number
  week5: number
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}


function text(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value)
}

function nullableText(value: unknown): string | null {
  return value == null ? null : String(value)
}

function mapDashboardRow(row: Record<string, unknown>): PedagogyDashboardRow {
  return {
    periodId: Number(row.period_id),
    specialistUserId: String(row.specialist_user_id),
    specialistName: nullableText(row.specialist_name),
    tgUsername: nullableText(row.tg_username),
    partnersCount: Number(row.partners_count ?? 0),
    studiosCount: Number(row.studios_count ?? 0),
    plannedReviewsCount: Number(row.planned_reviews_count ?? 0),
    factReviewsCount: Number(row.fact_reviews_count ?? 0),
    completionPct: row.completion_pct == null ? null : Number(row.completion_pct),
    studiosWithoutPlanCount: Number(row.studios_without_plan_count ?? 0),
    factsWithoutPlanCount: Number(row.facts_without_plan_count ?? 0),
    lowQualityReviewsCount: Number(row.low_quality_reviews_count ?? 0),
    latestReviewDate: nullableText(row.latest_review_date),
    pedagogyRoleEndDate: nullableText(row.pedagogy_role_end_date),
  }
}

function mapPeriodRow(row: Record<string, unknown>): PedagogySpecialistPeriodRow {
  return {
    periodId: Number(row.period_id),
    specialistUserId: String(row.specialist_user_id),
    studioInternalId: Number(row.studio_internal_id),
    studioNumber: row.studio_number == null ? '' : String(row.studio_number),
    cityName: text(row.city_name),
    address: text(row.address),
    statusCode: text(row.status_code, 'OPEN'),
    partnerNumber: row.partner_number == null ? null : Number(row.partner_number),
    partnerPersonsFio: nullableText(row.partner_persons_fio),
    partnerCitiesText: nullableText(row.partner_cities_text),
    plannedReviewsCount: Number(row.planned_reviews_count ?? 0),
    factReviewsCount: Number(row.fact_reviews_count ?? 0),
    latestReviewDate: nullableText(row.latest_review_date),
    hasPlan: Boolean(row.has_plan),
    inScope: Boolean(row.in_scope),
    missingPlan: Boolean(row.missing_plan),
    factWithoutPlan: Boolean(row.fact_without_plan),
    closedButKept: Boolean(row.closed_but_kept),
  }
}

function mapWeeklyPlanMatrixRow(row: Record<string, unknown>): PedagogyWeeklyPlanMatrixRow {
  return {
    periodId: Number(row.period_id),
    studioInternalId: Number(row.studio_internal_id),
    studioNumber: row.studio_number == null ? '' : String(row.studio_number),
    cityName: text(row.city_name),
    address: text(row.address),
    statusCode: text(row.status_code, 'OPEN'),
    partnerNumber: row.partner_number == null ? null : Number(row.partner_number),
    partnerPersonsFio: nullableText(row.partner_persons_fio),
    partnerCitiesText: nullableText(row.partner_cities_text),
    inScope: Boolean(row.in_scope),
    canPlanWeek1: Boolean(row.can_plan_week_1),
    canPlanWeek2: Boolean(row.can_plan_week_2),
    canPlanWeek3: Boolean(row.can_plan_week_3),
    canPlanWeek4: Boolean(row.can_plan_week_4),
    canPlanWeek5: Boolean(row.can_plan_week_5),
    planWeek1: Number(row.plan_week_1 ?? 0),
    planWeek2: Number(row.plan_week_2 ?? 0),
    planWeek3: Number(row.plan_week_3 ?? 0),
    planWeek4: Number(row.plan_week_4 ?? 0),
    planWeek5: Number(row.plan_week_5 ?? 0),
    factWeek1: Number(row.fact_week_1 ?? 0),
    factWeek2: Number(row.fact_week_2 ?? 0),
    factWeek3: Number(row.fact_week_3 ?? 0),
    factWeek4: Number(row.fact_week_4 ?? 0),
    factWeek5: Number(row.fact_week_5 ?? 0),
    planTotal: Number(row.plan_total ?? 0),
    factTotal: Number(row.fact_total ?? 0),
  }
}

export async function listPedagogyDashboard(periodId: number | null, weekNumber: number | null): Promise<PedagogyDashboardRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_specialists_dashboard', {
      p_period_id: periodId,
      p_week_number: weekNumber,
    })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(mapDashboardRow)
}

export async function getPedagogyDepartmentQualitySummary(
  periodId: number | null,
  weekNumber: number | null
): Promise<PedagogyDepartmentQualitySummary> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_department_quality_summary', {
      p_period_id: periodId,
      p_week_number: weekNumber,
    })
  )

  if (error) throw new Error(formatError(error))
  const row = ((data ?? []) as Array<Record<string, unknown>>)[0]
  return {
    reviewsCount: Number(row?.reviews_count ?? 0),
    totalScore: Number(row?.total_score ?? 0),
    maxScore: Number(row?.max_score ?? 0),
    averageQualityPercent: row?.average_quality_percent == null ? null : Number(row.average_quality_percent),
  }
}

export async function getPedagogySpecialistPeriodDetails(
  specialistUserId: string,
  periodId: number | null,
  weekNumber: number | null
): Promise<PedagogySpecialistPeriodRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_specialist_period_details', {
      p_specialist_user_id: specialistUserId,
      p_period_id: periodId,
      p_week_number: weekNumber,
    })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(mapPeriodRow)
}

export async function getPedagogySpecialistWeeklyPlanMatrix(
  specialistUserId: string,
  periodId: number | null
): Promise<PedagogyWeeklyPlanMatrixRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_specialist_weekly_plan_matrix', {
      p_specialist_user_id: specialistUserId,
      p_period_id: periodId,
    })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(mapWeeklyPlanMatrixRow)
}

export async function savePedagogyWeeklyPlan(input: {
  specialistUserId: string
  periodId: number
  rows: SavePedagogyWeeklyPlanRowInput[]
}): Promise<number> {
  const supabase = getSupabase()
  const payload = input.rows.map((row) => ({
    studio_internal_id: row.studioInternalId,
    week_1: row.week1,
    week_2: row.week2,
    week_3: row.week3,
    week_4: row.week4,
    week_5: row.week5,
  }))

  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('upsert_pedagogy_weekly_review_plan', {
      p_specialist_user_id: input.specialistUserId,
      p_period_id: input.periodId,
      p_rows: payload,
    })
  )

  if (error) throw new Error(formatError(error))
  return Number(data ?? 0)
}
